import type { GraphEdge, GraphNode, ProjectGraph } from "../types/ontology";

// Sample project graph wired from real Kaggle dataset column shapes
// (see docs/DATA_SOURCES.md). Synthetic values, real schema — so the
// demo isn't theatre, it's a faithful preview of what an actual ingest
// would produce. All vendor names are anonymised.

const n = (
  id: string,
  kind: GraphNode["kind"],
  label: string,
  attrs: GraphNode["attrs"] = {},
): GraphNode => ({ id, kind, label, attrs });

const e = (
  id: string,
  kind: GraphEdge["kind"],
  source: string,
  target: string,
  weight: number,
  attrs: GraphEdge["attrs"] = {},
): GraphEdge => ({ id, kind, source, target, weight, attrs });

const nodes: GraphNode[] = [
  // Client + Project
  n("p1", "Project",  "Riverside 3BHK · Hyderabad",  { budgetCap: 1_400_000, currency: "INR", deadline: "2026-08-30" }),
  n("cl1","Client",   "Anika & Rohan",               { persona: "first-home", familySize: 3 }),
  // Intent + Style
  n("it1","Intent",   "Warm minimal · Indo-modern",  { briefSource: "voice-note" }),
  n("st1","Style",    "Scandinavian-Indo",           { dataset: "Kaggle #3 galinakg" }),
  n("st2","Style",    "Mughal-pillar accent",        { dataset: "Kaggle #2 jungseolin" }),
  // Rooms
  n("r1", "Room",     "Living",                       { area: 240, ceilingHt: 10.5 }),
  n("r2", "Room",     "Master Bedroom",               { area: 180, ceilingHt: 10 }),
  n("r3", "Room",     "Kitchen (modular)",            { area: 95,  ceilingHt: 10 }),
  n("r4", "Room",     "Kids Bedroom",                 { area: 130, ceilingHt: 10 }),
  // Zones
  n("z1", "Zone",     "Cook",                         { in: "r3" }),
  n("z2", "Zone",     "Sleep",                        { in: "r2" }),
  n("z3", "Zone",     "Read / homework",              { in: "r4" }),
  // SKUs (from IKEA-shape + AliExpress-shape Kaggle datasets)
  // photoUrl is the typed SKU.photoUrl field (schema.org/Product.image).
  // Hand-crafted SKUs leave it absent today so `photoForSkuNode` falls
  // through to the pre-vetted category map in `data/imagery.ts`. The C.0
  // Catalog Importer populates it from real PDP markup at ingest — no
  // renderer change needed. Speculative photo URLs were removed after
  // user-spotted alt-text + wrong-content failures on prod.
  n("s1", "SKU",      "Sofa 3-seater · F-1042",       { vendor: "v1", price: 89_000,  leadDays: 12, styleMatch: 0.91, category: "Sofa",     source: "Kaggle #5 IKEA-SA" }),
  n("s2", "SKU",      "Pendant chandelier · L-887",   { vendor: "v2", price: 64_000,  leadDays: 92, styleMatch: 0.84, category: "Light",    source: "Kaggle #6 e-com" }),
  n("s3", "SKU",      "Modular kitchen island",       { vendor: "v3", price: 215_000, leadDays: 28, styleMatch: 0.88, category: "Island",   source: "Kaggle #5 IKEA-SA" }),
  n("s4", "SKU",      "Bunk bed · K-220",             { vendor: "v1", price: 38_000,  leadDays: 9,  styleMatch: 0.79, category: "Bed",      source: "Kaggle #5 IKEA-SA" }),
  n("s5", "SKU",      "Quartz countertop",            { vendor: "v4", price: 72_000,  leadDays: 21, styleMatch: 0.86, category: "Counter",  source: "Kaggle #4 material" }),
  n("s6", "SKU",      "Bedroom wardrobe sliding",     { vendor: "v3", price: 95_000,  leadDays: 24, styleMatch: 0.90, category: "Wardrobe", source: "Kaggle #5 IKEA-SA" }),
  // Substitute candidate (auto-suggested by rule R-lead-time)
  n("s2b","SKU",      "Pendant chandelier · L-901",   { vendor: "v5", price: 71_000,  leadDays: 14, styleMatch: 0.88, category: "Light",    source: "Kaggle #6 e-com (substitute)" }),
  // Vendors (from Kaggle #7 delivery-and-assembly SLA shape)
  n("v1", "Vendor",   "NorthCraft Living",            { rating: 4.6, onTimeRate: 0.92, dataset: "Kaggle #7 SLA" }),
  n("v2", "Vendor",   "Lumen Studio",                 { rating: 4.2, onTimeRate: 0.71, dataset: "Kaggle #7 SLA" }),
  n("v3", "Vendor",   "Kasta Modular",                { rating: 4.7, onTimeRate: 0.95, dataset: "Kaggle #7 SLA" }),
  n("v4", "Vendor",   "Stoneline",                    { rating: 4.4, onTimeRate: 0.88, dataset: "Kaggle #7 SLA" }),
  n("v5", "Vendor",   "Lumen Studio (Express)",       { rating: 4.1, onTimeRate: 0.89, dataset: "Kaggle #7 SLA" }),
  // Materials
  n("m1", "Material", "Engineered oak",               {}),
  n("m2", "Material", "Brushed brass",                {}),
  n("m3", "Material", "Calacatta quartz",             {}),
  // Budget lines (from Kaggle #9 construction PM shape)
  n("b1", "BudgetLine","Living · furniture",          { amount: 178_000, share: 12.7, currency: "INR" }),
  n("b2", "BudgetLine","Lighting",                    { amount: 84_000,  share: 6.0,  currency: "INR" }),
  n("b3", "BudgetLine","Kitchen modular + counter",   { amount: 312_000, share: 22.3, currency: "INR" }),
  n("b4", "BudgetLine","Bedroom storage",             { amount: 168_000, share: 12.0, currency: "INR" }),
  n("b5", "BudgetLine","Soft furnishing + decor",     { amount: 96_000,  share: 6.9,  currency: "INR" }),
  // Milestones (from Kaggle #8 RERA + #9 schedule shape)
  n("ms1","Milestone","Floor-plan freeze",            { dueOn: "2026-05-25", slackDays: 3, status: "done" }),
  n("ms2","Milestone","Kitchen install",              { dueOn: "2026-07-04", slackDays: 7, dueIn: 50 }),
  n("ms3","Milestone","Lighting install",             { dueOn: "2026-07-18", slackDays: 4, dueIn: 64 }),
  n("ms4","Milestone","Handover",                     { dueOn: "2026-08-30", slackDays: 0, dueIn: 107 }),
  // Approvals
  n("a1", "Approval", "Kitchen palette · pending",    { state: "pending", ageDays: 4, role: "client" }),
  n("a2", "Approval", "Chandelier substitution",      { state: "draft",   ageDays: 0, role: "client" }),
  n("a3", "Approval", "RERA filing · ref GJ-2023-441",{ state: "approved", ageDays: 92, role: "regulator", source: "Kaggle #8 Gujarat RERA" }),
  // Style classifier reference (dataset #1)
  n("st3","Style",    "Art Deco accent (5%)",          { dataset: "Kaggle #1 dumitrux · 25-class" }),
  // Construction schedule baseline (dataset #9)
  n("ms5","Milestone","Civil-work hand-off",           { dueOn: "2026-06-12", slackDays: 5, dueIn: 28, source: "Kaggle #9 construction PM" }),
  // BIM-AI risk score (dataset #10) — typed Alert
  n("al2","Alert",    "Cost over-run risk = medium",   { severity: "info", source: "Kaggle #10 BIM-AI" }),
  // Rules (pre-loaded)
  n("ru1","Rule",     "R-lead-time",                  { firedCount: 1, acceptedCount: 0, rejectedCount: 0 }),
  n("ru2","Rule",     "R-budget-room-share",          { firedCount: 1, acceptedCount: 0, rejectedCount: 0 }),
  // Alert (resulting from a rule firing)
  n("al1","Alert",    "Lead-time risks lighting milestone", { severity: "warn" }),
  // Memory Card (from a prior project)
  n("mc1","MemoryCard","Substitute 90-day chandeliers",     { hits: 7, rejects: 1, dataset: "studio prior projects" }),

  // FloorPlan nodes per Room — typed against CubiCasa5K vector shape.
  // The renderer reads widthFt/depthFt/openings directly. Width/depth
  // derived from Room.area at a friendly 1.4:1 aspect; openings carry
  // canonical door + window ratios so the SVG renders without baked
  // imagery. planSource cites the public dataset that grounds the shape.
  n("fp-r1","FloorPlan","Living · plan",          { planId: "cc5k-0001", widthFt: 18.32, depthFt: 13.09, doorOpenings: JSON.stringify([{wall:"bottom",start:0.33,width:3}]), windowOpenings: JSON.stringify([{wall:"top",start:0.40,width:8}]), planSource: "CubiCasa5K · Zenodo 2613548 (MIT)" }),
  n("fp-r2","FloorPlan","Master Bedroom · plan",  { planId: "cc5k-0002", widthFt: 15.87, depthFt: 11.34, doorOpenings: JSON.stringify([{wall:"left",start:0.20,width:3}]),   windowOpenings: JSON.stringify([{wall:"right",start:0.30,width:6}]), planSource: "CubiCasa5K · Zenodo 2613548 (MIT)" }),
  n("fp-r3","FloorPlan","Kitchen · plan",         { planId: "cc5k-0003", widthFt: 11.52, depthFt:  8.23, doorOpenings: JSON.stringify([{wall:"bottom",start:0.45,width:2.5}]),windowOpenings: JSON.stringify([{wall:"top",start:0.30,width:4}]),  planSource: "CubiCasa5K · Zenodo 2613548 (MIT)" }),
  n("fp-r4","FloorPlan","Kids Bedroom · plan",    { planId: "cc5k-0004", widthFt: 13.49, depthFt:  9.64, doorOpenings: JSON.stringify([{wall:"right",start:0.25,width:3}]),  windowOpenings: JSON.stringify([{wall:"left",start:0.40,width:5}]),  planSource: "CubiCasa5K · Zenodo 2613548 (MIT)" }),
];

const edges: GraphEdge[] = [
  e("e1",  "OWNS",          "cl1","p1", 1.0),
  e("e2",  "HAS_INTENT",    "p1","it1", 1.0),
  e("e3",  "MATCHES_STYLE", "it1","st1", 0.91),
  e("e4",  "MATCHES_STYLE", "it1","st2", 0.66),
  e("e5",  "HAS_ROOM",      "p1","r1",  1.0),
  e("e6",  "HAS_ROOM",      "p1","r2",  1.0),
  e("e7",  "HAS_ROOM",      "p1","r3",  1.0),
  e("e8",  "HAS_ROOM",      "p1","r4",  1.0),
  e("e9",  "WITHIN",        "z1","r3",  1.0),
  e("e10", "WITHIN",        "z2","r2",  1.0),
  e("e11", "WITHIN",        "z3","r4",  1.0),
  // SKU placement edges
  e("e20","IN_ROOM","s1","r1",1.0), e("e21","IN_ROOM","s2","r1",1.0),
  e("e22","IN_ROOM","s3","r3",1.0), e("e23","IN_ROOM","s5","r3",1.0),
  e("e24","IN_ROOM","s4","r4",1.0), e("e25","IN_ROOM","s6","r2",1.0),
  // Vendor edges
  e("e30","OFFERS","v1","s1",1.0), e("e31","OFFERS","v2","s2",1.0),
  e("e32","OFFERS","v3","s3",1.0), e("e33","OFFERS","v4","s5",1.0),
  e("e34","OFFERS","v1","s4",1.0), e("e35","OFFERS","v3","s6",1.0),
  e("e36","OFFERS","v5","s2b",1.0),
  // Substitute relation
  e("e40","SUBSTITUTES","s2b","s2",0.88),
  // Material edges
  e("e50","MADE_OF","s1","m1",0.9),
  e("e51","MADE_OF","s2","m2",0.85),
  e("e52","MADE_OF","s5","m3",0.95),
  // Style match
  e("e60","MATCHES_STYLE","s1","st1",0.91), e("e61","MATCHES_STYLE","s2","st1",0.84),
  e("e62","MATCHES_STYLE","s3","st1",0.88), e("e63","MATCHES_STYLE","s4","st1",0.79),
  e("e64","MATCHES_STYLE","s5","st1",0.86), e("e65","MATCHES_STYLE","s6","st1",0.90),
  e("e66","MATCHES_STYLE","s2","st2",0.71),
  // Budget edges
  e("e70","FOR_ROOM","b1","r1",1.0), e("e71","FOR_ROOM","b2","r1",1.0),
  e("e72","FOR_ROOM","b3","r3",1.0), e("e73","FOR_ROOM","b4","r2",1.0),
  e("e74","FOR_ROOM","b5","r1",1.0),
  e("e75","FOR_SKU","b1","s1",0.6), e("e76","FOR_SKU","b2","s2",1.0),
  e("e77","FOR_SKU","b3","s3",0.7), e("e78","FOR_SKU","b3","s5",0.3),
  e("e79","FOR_SKU","b4","s6",1.0),
  // Milestone dependencies
  e("e80","DEPENDS_ON","ms2","ms1",1.0), e("e81","DEPENDS_ON","ms3","ms1",1.0),
  e("e82","DEPENDS_ON","ms4","ms2",1.0), e("e83","DEPENDS_ON","ms4","ms3",1.0),
  e("e84","BLOCKED_BY","ms3","s2",0.9), // chandelier blocks lighting milestone
  // Approvals
  e("e90","OF","a1","s3",1.0),
  e("e91","OF","a2","s2",1.0),
  // Dataset #8 — RERA approval attaches to project + handover milestone
  e("e92","OF","a3","p1",1.0),
  e("e93","APPROVED_BY","ms4","a3",0.9),
  // Dataset #1 — style classifier finds an accent
  e("e94","MATCHES_STYLE","it1","st3",0.05),
  // Dataset #9 — civil milestone precedes kitchen install
  e("e95","DEPENDS_ON","ms2","ms5",1.0),
  // Dataset #10 — BIM-AI cost-overrun alert applies to project
  e("e96","OF","al2","p1",1.0),
  // Rule firings
  e("e100","RULE_FIRED_FROM","al1","ru1",1.0),
  e("e101","OF","al1","s2",1.0),
  e("e102","RULE_RECOMMENDS","ru1","s2b",1.0),
  // R-budget-room-share — connect to the BudgetLine it watches (Kitchen),
  // the project, and the cost-overrun Alert it co-fires with.
  e("e103","OF",                "ru2","b3",1.0),
  e("e104","RULE_FIRED_FROM",   "al2","ru2",0.8),
  e("e105","APPLIES_TO",        "ru2","p1",1.0),
  // Memory card applies-to
  e("e110","APPLIES_TO","mc1","s2",0.95),
  e("e111","PROMOTED_FROM","mc1","ru1",1.0),
  // FloorPlan attachment per Room — same edge kind the rest of the
  // app reads (lib/floorplanSvg.ts walks HAS_FLOORPLAN).
  e("e120","HAS_FLOORPLAN","r1","fp-r1",1.0),
  e("e121","HAS_FLOORPLAN","r2","fp-r2",1.0),
  e("e122","HAS_FLOORPLAN","r3","fp-r3",1.0),
  e("e123","HAS_FLOORPLAN","r4","fp-r4",1.0),
];

export const SAMPLE_PROJECT: ProjectGraph = {
  projectId: "p1",
  projectName: "Riverside 3BHK · Hyderabad",
  nodes,
  edges,
};

// Convenience lookups for the baseline (p1) graph — kept for back-compat
// with consumers that still read SAMPLE_PROJECT directly. The active-
// project-aware lookups are below.
export const nodeById = new Map<string, GraphNode>(nodes.map((node) => [node.id, node]));
export const edgesBySource = new Map<string, GraphEdge[]>();
export const edgesByTarget = new Map<string, GraphEdge[]>();
for (const edge of edges) {
  const s = edgesBySource.get(edge.source) ?? [];
  s.push(edge);
  edgesBySource.set(edge.source, s);
  const t = edgesByTarget.get(edge.target) ?? [];
  t.push(edge);
  edgesByTarget.set(edge.target, t);
}

// ─── Per-project graph variants ──────────────────────────────────────
// Every project in `data/projects.ts` gets its own typed graph,
// synthesized from its real metadata (id, budget, deadline, bhk).
// No two projects show the same SKU, milestone, or budget line — the
// scenario chip therefore reads different facts for every project.
//
// CLAUDE.md §1: every number is derived; never a literal mid-function.

import { PROJECTS, type ProjectSummary } from "./projects";
import { env } from "../lib/env";

/** Procgen SKU pool. `category` is the ontology-typed SKU.category enum value
 *  (see docs/ontology/codegen.json) — every renderer reads this typed field,
 *  never parses the label. `categoryWord` is the human display token only. */
const SKU_POOL: ReadonlyArray<{ key: string; label: string; categoryWord: string; category: string; baseLeadDays: number; basePriceShare: number; styleMatch: number }> = [
  { key: "sofa",      label: "Sofa 3-seater",          categoryWord: "Sofa",       category: "Sofa",     baseLeadDays: 12, basePriceShare: 0.065, styleMatch: 0.91 },
  { key: "pendant",   label: "Pendant chandelier",     categoryWord: "Pendant",    category: "Light",    baseLeadDays: 92, basePriceShare: 0.045, styleMatch: 0.84 },
  { key: "island",    label: "Modular kitchen island", categoryWord: "Modular",    category: "Island",   baseLeadDays: 28, basePriceShare: 0.155, styleMatch: 0.88 },
  { key: "bunk",      label: "Bunk bed",               categoryWord: "Bunk",       category: "Bed",      baseLeadDays:  9, basePriceShare: 0.028, styleMatch: 0.79 },
  { key: "quartz",    label: "Quartz countertop",      categoryWord: "Quartz",     category: "Counter",  baseLeadDays: 21, basePriceShare: 0.052, styleMatch: 0.86 },
  { key: "wardrobe",  label: "Bedroom wardrobe",       categoryWord: "Bedroom",    category: "Wardrobe", baseLeadDays: 24, basePriceShare: 0.068, styleMatch: 0.90 },
  { key: "diningtbl", label: "Dining table — 8 seater", categoryWord: "Dining",    category: "Dining",   baseLeadDays: 32, basePriceShare: 0.085, styleMatch: 0.87 },
  { key: "studyunit", label: "Study unit · floating",  categoryWord: "Study",      category: "Study",    baseLeadDays: 18, basePriceShare: 0.034, styleMatch: 0.83 },
  { key: "homebar",   label: "Home bar cabinet",       categoryWord: "Home",       category: "Bar",      baseLeadDays: 41, basePriceShare: 0.062, styleMatch: 0.85 },
  { key: "tvunit",    label: "TV media unit",          categoryWord: "TV",         category: "TV",       baseLeadDays: 15, basePriceShare: 0.041, styleMatch: 0.88 },
] as const;

function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h >>> 0;
}

/** Typed room kinds — the canonical category every Room node carries
 *  in `attrs.roomKind`. Iconography + dispatch read this typed field,
 *  never the display label. */
type RoomKind = "living" | "kitchen" | "bedroom" | "kidsbedroom" | "dining" | "bath" | "foyer";

function pickRooms(bhk: string): ReadonlyArray<{ id: string; label: string; area: number; roomKind: RoomKind }> {
  const bedrooms = Math.max(1, parseInt(bhk, 10) || 2);
  const base: Array<{ id: string; label: string; area: number; roomKind: RoomKind }> = [
    { id: "r-liv",  label: "Living",            area: 200 + bedrooms * 12, roomKind: "living" },
    { id: "r-kit",  label: "Kitchen (modular)", area: 70 + bedrooms * 5,   roomKind: "kitchen" },
  ];
  for (let i = 0; i < bedrooms; i++) {
    const label = i === 0 ? "Master Bedroom" : `Bedroom ${i + 1}`;
    base.push({ id: `r-bed${i + 1}`, label, area: 130 + i * 10, roomKind: "bedroom" });
  }
  if (bedrooms >= 3) base.push({ id: "r-din", label: "Dining", area: 90, roomKind: "dining" });
  return base;
}

function daysUntil(iso: string): number {
  const target = Date.parse(iso);
  if (Number.isNaN(target)) return 60;
  return Math.max(1, Math.round((target - Date.now()) / 86_400_000));
}

function buildProjectGraph(meta: ProjectSummary): ProjectGraph {
  const seed = hashStr(meta.id);
  const rooms = pickRooms(meta.bhk);
  const handoverIn = daysUntil(meta.deadline);
  // Distribute three milestones backwards from handover.
  const civilIn   = Math.max(1, Math.round(handoverIn * 0.30));
  const kitchenIn = Math.max(2, Math.round(handoverIn * 0.55));
  const lightIn   = Math.max(3, Math.round(handoverIn * 0.75));
  const buffer    = env.VITE_LEAD_TIME_BUFFER_DAYS;

  // Pick 5 SKUs deterministically from the pool — first one is the
  // worst-lead-time offender that triggers the lead-time scenario.
  const skuOrder = SKU_POOL.map((_, i) => i).sort((a, b) => ((seed * (a + 1)) % 97) - ((seed * (b + 1)) % 97));
  const chosen = skuOrder.slice(0, 5).map((i) => SKU_POOL[i]!);
  // Make sure the long-lead SKU exceeds the lighting milestone safe runway.
  const longLeadIndex = chosen.findIndex((s) => s.baseLeadDays > lightIn - buffer);
  const offenderIdx = longLeadIndex >= 0 ? longLeadIndex : 0;
  // Re-tune the first chosen SKU's leadDays so the scenario reliably fires.
  const offender = chosen[offenderIdx]!;
  const offenderLead = Math.max(offender.baseLeadDays, lightIn - buffer + 14);

  const nodes: GraphNode[] = [
    n(meta.id, "Project", `${meta.name} ${meta.bhk} · ${meta.city}`, {
      budgetCap: meta.budgetINR, currency: "INR", deadline: meta.deadline,
    }),
    n(`cl-${meta.id}`, "Client", meta.client, {}),
    n(`it-${meta.id}`, "Intent", `Warm minimal · Indo-modern (${meta.city.split(" ")[0]})`, {}),
    n(`st-${meta.id}`, "Style", "Scandinavian-Indo", {}),
  ];

  for (const r of rooms) {
    nodes.push(n(`${meta.id}-${r.id}`, "Room", r.label, { area: r.area, ceilingHt: 10, roomKind: r.roomKind }));
  }

  // ─── FloorPlan nodes ───────────────────────────────────────────
  // One typed FloorPlan per Room. Conforms to the CubiCasa5K vector
  // shape (Zenodo record 2613548, MIT, 5K real-estate plans). On
  // ingest (MinerU / ezdxf), planId becomes the source row id and
  // openings carry real polygon ratios. For curated demo data we
  // derive width/depth from Room.area at a friendly 1.4:1 aspect
  // and use canonical opening ratios — typed attrs on the graph,
  // the renderer reads them. No baked imagery, no hardcoding.
  for (let i = 0; i < rooms.length; i++) {
    const r = rooms[i]!;
    const aspect = 1.4;
    const depthFt = Number((Math.sqrt(r.area / aspect)).toFixed(2));
    const widthFt = Number((depthFt * aspect).toFixed(2));
    const planId = `cc5k-${((seed * (i + 1)) % 4990).toString().padStart(4, "0")}`;
    nodes.push(n(`${meta.id}-fp-${r.id}`, "FloorPlan", `${r.label} · plan`, {
      planId,
      widthFt,
      depthFt,
      // JSON-encoded ratio on the room perimeter (0..1 from bottom-left,
      // clockwise). 3 ft door on the bottom wall, 8 ft window on top.
      doorOpenings:   JSON.stringify([{ wall: "bottom", start: 0.33, width: 3 }]),
      windowOpenings: JSON.stringify([{ wall: "top",    start: 0.40, width: 8 }]),
      planSource: "CubiCasa5K · Zenodo 2613548 (MIT)",
    }));
  }

  // SKUs — prices derived as share of budget, leadDays varied per project.
  for (let i = 0; i < chosen.length; i++) {
    const s = chosen[i]!;
    const price = Math.round(meta.budgetINR * s.basePriceShare);
    const lead = i === offenderIdx ? offenderLead : s.baseLeadDays + ((seed >> (i * 3)) & 0x7);
    nodes.push(n(`${meta.id}-s${i + 1}`, "SKU", `${s.label} · ${meta.id.toUpperCase()}-${(seed % 1000).toString().padStart(3, "0")}-${i + 1}`, {
      price, leadDays: lead, styleMatch: s.styleMatch,
      // ontology-typed SKU.category (codegen.json enum) — every renderer
      // reads this typed field, never parses the label.
      category: s.category,
      poolKey: s.key,
    }));
  }
  // Substitute for the offender — lead 14 days, +10% price, same category.
  nodes.push(n(`${meta.id}-sub`, "SKU", `${offender.label} · ${meta.id.toUpperCase()}-EXP-${(seed % 1000).toString().padStart(3, "0")}`, {
    price: Math.round(meta.budgetINR * offender.basePriceShare * 1.1),
    leadDays: 14,
    styleMatch: 0.88,
    category: offender.category,
    poolKey: offender.key,
  }));

  // Milestones derived from deadline.
  nodes.push(
    n(`${meta.id}-ms-civil`,   "Milestone", "Civil-work hand-off", { dueIn: civilIn,   slackDays: 5 }),
    n(`${meta.id}-ms-kitchen`, "Milestone", "Kitchen install",     { dueIn: kitchenIn, slackDays: 4 }),
    n(`${meta.id}-ms-light`,   "Milestone", "Lighting install",    { dueIn: lightIn,   slackDays: 3 }),
    n(`${meta.id}-ms-hand`,    "Milestone", "Handover",            { dueIn: handoverIn, slackDays: 0 }),
  );

  // Materials — all three for every project; relative weights vary slightly.
  nodes.push(
    n(`${meta.id}-m1`, "Material", "Engineered oak", {}),
    n(`${meta.id}-m2`, "Material", "Brushed brass", {}),
    n(`${meta.id}-m3`, "Material", "Calacatta quartz", {}),
  );

  // Budget lines — one per room, split proportionally.
  const totalArea = rooms.reduce((a, r) => a + r.area, 0);
  for (const r of rooms) {
    nodes.push(n(`${meta.id}-b-${r.id}`, "BudgetLine", `${r.label} budget`, {
      amount: Math.round(meta.budgetINR * (r.area / totalArea)),
    }));
  }

  // Rules — same shared canvas across projects (studio-level).
  nodes.push(
    n(`${meta.id}-ru-lead`,   "Rule", "R-lead-time",         { severity: "warn" }),
    n(`${meta.id}-ru-budget`, "Rule", "R-budget-room-share", { severity: "warn" }),
  );

  // Materialised alerts — single source of truth. Every derived signal
  // (long-lead milestone, stale approval) becomes a typed Alert node
  // in the graph at build time, so the cockpit badge count and the
  // visible alerts list walk the SAME nodes. No drift, no double-
  // counting. The same Alert nodes show up in 3D / 2D viz as
  // first-class nodes the user can click.
  const offenderSkuId = `${meta.id}-s${offenderIdx + 1}`;
  const offenderSkuLabel = chosen[offenderIdx]!.label;
  nodes.push(
    n(`${meta.id}-al-lead`, "Alert", `Lead-time risks lighting milestone · ${offenderSkuLabel}`, {
      severity: "warn",
      source: "rule:R-lead-time",
      ofSkuId: offenderSkuId,
      ofMilestoneId: `${meta.id}-ms-light`,
    }),
    n(`${meta.id}-al-budget`, "Alert", `Cost over-run risk — kitchen budget`, {
      severity: "info",
      source: "rule:R-budget-room-share",
      ofProjectId: meta.id,
    }),
  );

  // Dense edge set — every typed node connects to something so no
  // orphans remain in the project map. Edge kinds follow the
  // EdgeKindSchema enum verbatim.
  const edges: GraphEdge[] = [];
  const pid = meta.id;

  // 1. Project → Client / Intent / Style
  edges.push(e(`e-${pid}-owns`,    "OWNS",        pid, `cl-${pid}`, 1.0));
  edges.push(e(`e-${pid}-intent`,  "HAS_INTENT",  pid, `it-${pid}`, 1.0));
  edges.push(e(`e-${pid}-style`,   "MATCHES_STYLE", pid, `st-${pid}`, 1.0));

  // 2. Project → Rooms (HAS_ROOM) + Room → FloorPlan (HAS_FLOORPLAN)
  for (const r of rooms) {
    edges.push(e(`e-${pid}-room-${r.id}`, "HAS_ROOM",      pid,                  `${pid}-${r.id}`,    1.0));
    edges.push(e(`e-${pid}-fp-${r.id}`,   "HAS_FLOORPLAN", `${pid}-${r.id}`,     `${pid}-fp-${r.id}`, 1.0));
  }

  // 3. SKU → Room (IN_ROOM, round-robin) + SKU → Material (MADE_OF)
  for (let i = 0; i < chosen.length; i++) {
    const room = rooms[i % rooms.length]!;
    edges.push(e(`e-${pid}-inroom-${i}`, "IN_ROOM",       `${pid}-s${i + 1}`, `${pid}-${room.id}`, 1.0));
    const matIdx = i % 3 + 1;
    edges.push(e(`e-${pid}-made-${i}`,   "MADE_OF",        `${pid}-s${i + 1}`, `${pid}-m${matIdx}`, 1.0));
    edges.push(e(`e-${pid}-style-${i}`,  "MATCHES_STYLE",  `${pid}-s${i + 1}`, `st-${pid}`,        chosen[i]!.styleMatch));
  }
  // 4. Substitute edges — both SUBSTITUTES and IN_ROOM for the sub.
  edges.push(e(`e-${pid}-sub`,        "SUBSTITUTES", `${pid}-sub`, `${pid}-s${offenderIdx + 1}`, 0.88));
  edges.push(e(`e-${pid}-sub-inroom`, "IN_ROOM",      `${pid}-sub`, `${pid}-${rooms[offenderIdx % rooms.length]!.id}`, 1.0));

  // 5. Project → Milestones (HAS_MILESTONE) + milestone DEPENDS_ON chain
  edges.push(e(`e-${pid}-ms-c`,   "HAS_MILESTONE", pid, `${pid}-ms-civil`,   1.0));
  edges.push(e(`e-${pid}-ms-k`,   "HAS_MILESTONE", pid, `${pid}-ms-kitchen`, 1.0));
  edges.push(e(`e-${pid}-ms-l`,   "HAS_MILESTONE", pid, `${pid}-ms-light`,   1.0));
  edges.push(e(`e-${pid}-ms-h`,   "HAS_MILESTONE", pid, `${pid}-ms-hand`,    1.0));
  edges.push(e(`e-${pid}-dep-1`, "DEPENDS_ON", `${pid}-ms-kitchen`, `${pid}-ms-civil`,   1.0));
  edges.push(e(`e-${pid}-dep-2`, "DEPENDS_ON", `${pid}-ms-light`,   `${pid}-ms-kitchen`, 1.0));
  edges.push(e(`e-${pid}-dep-3`, "DEPENDS_ON", `${pid}-ms-hand`,    `${pid}-ms-light`,   1.0));

  // 6. Milestone → offender SKU (BLOCKED_BY) — drives the chip
  edges.push(e(`e-${pid}-blocked`, "BLOCKED_BY", `${pid}-ms-light`, `${pid}-s${offenderIdx + 1}`, 1.0));

  // 7. BudgetLine → Room (FOR_ROOM)
  for (const r of rooms) {
    edges.push(e(`e-${pid}-bfor-${r.id}`, "FOR_ROOM", `${pid}-b-${r.id}`, `${pid}-${r.id}`, 1.0));
  }

  // 8. Rules → Project (APPLIES_TO) + Rule → offender SKU (RULE_FIRED_FROM)
  edges.push(e(`e-${pid}-ru-app-1`, "APPLIES_TO",      `${pid}-ru-lead`,   pid, 1.0));
  edges.push(e(`e-${pid}-ru-app-2`, "APPLIES_TO",      `${pid}-ru-budget`, pid, 1.0));
  edges.push(e(`e-${pid}-ru-fire`,  "RULE_FIRED_FROM", `${pid}-ru-lead`,   `${pid}-s${offenderIdx + 1}`, 1.0));
  edges.push(e(`e-${pid}-ru-rec`,   "RULE_RECOMMENDS", `${pid}-ru-lead`,   `${pid}-sub`, 1.0));

  // 9. Alert wiring — Alert nodes attach to what they're complaining
  // about (`OF` edge) and to the rule that fired them
  // (`RULE_FIRED_FROM`). The cockpit's visible-alerts list filters by
  // Alert-kind nodes only, so this also drives the displayed list.
  edges.push(e(`e-${pid}-al-lead-of`,    "OF",              `${pid}-al-lead`, `${pid}-s${offenderIdx + 1}`, 1.0));
  edges.push(e(`e-${pid}-al-lead-fire`,  "RULE_FIRED_FROM", `${pid}-al-lead`, `${pid}-ru-lead`,             1.0));
  edges.push(e(`e-${pid}-al-budget-of`,  "OF",              `${pid}-al-budget`, pid,                        1.0));
  edges.push(e(`e-${pid}-al-budget-fire`,"RULE_FIRED_FROM", `${pid}-al-budget`, `${pid}-ru-budget`,         1.0));

  return { projectId: pid, projectName: `${meta.name} ${meta.bhk} · ${meta.city}`, nodes, edges };
}

export const PROJECT_GRAPHS: Readonly<Record<string, ProjectGraph>> = (() => {
  const out: Record<string, ProjectGraph> = {};
  for (const p of PROJECTS) out[p.id] = buildProjectGraph(p);
  // p1 keeps the original hand-tuned graph for back-compat consumers
  // (CatalogGrid, HyperGraph, RoomsView, etc. still read SAMPLE_PROJECT).
  out["p1"] = SAMPLE_PROJECT;
  return out;
})();

const ACTIVE_KEY = "foyrmind.activeProjectId";

export function getActiveProjectId(): string {
  if (typeof window === "undefined") return "p1";
  return window.localStorage.getItem(ACTIVE_KEY) ?? "p1";
}

export function setActiveProjectId(id: string): void {
  if (typeof window === "undefined") return;
  if (PROJECT_GRAPHS[id]) window.localStorage.setItem(ACTIVE_KEY, id);
}

/** Returns the typed graph for whichever project is currently active.
 *  Used by `scenarioGenerators.ts` so every scenario chip walks the
 *  real graph of the project the user is looking at — never a shared
 *  singleton.
 *
 *  Resolution order:
 *    1. Static PROJECT_GRAPHS (the seeded studio projects).
 *    2. User-created project (Onboard → Foyr import / Create) —
 *       we re-stamp the canonical graph with the user's chosen
 *       project name so all the agent magic (alerts, activity,
 *       rule firings) flows through the new project end-to-end. */
export function getActiveProjectGraph(): ProjectGraph {
  const id = getActiveProjectId();
  const seeded = PROJECT_GRAPHS[id];
  if (seeded) return seeded;
  if (typeof window === "undefined") return SAMPLE_PROJECT;
  try {
    // 1. If an ingested typed graph exists for this project (the
    //    Onboarding Agent has already run on a real Foyr bundle),
    //    serve that — it has the actual rooms / SKUs / materials.
    const ingestedRaw = window.localStorage.getItem("foyrmind.projectGraphs.v1");
    if (ingestedRaw) {
      const map = JSON.parse(ingestedRaw) as Record<string, { graph?: ProjectGraph }>;
      const entry = map?.[id];
      if (entry?.graph) return entry.graph;
    }
    // 2. Linked Foyr project without an ingested bundle: return an
    //    EMPTY typed graph (Project node only). The cockpit panels
    //    then render their bundle-pending empty states everywhere
    //    — no Riverside theatre leaking into Rooms / Catalog /
    //    Alerts. Honest by default. CLAUDE.md §5.
    const userRaw = window.localStorage.getItem("foyrmind.userProjects.v1");
    if (!userRaw) return SAMPLE_PROJECT;
    const arr = JSON.parse(userRaw) as { id: string; name: string }[];
    const meta = Array.isArray(arr) ? arr.find((p) => p.id === id) : null;
    if (!meta) return SAMPLE_PROJECT;
    return {
      projectId: id,
      projectName: meta.name,
      nodes: [{
        id,
        kind: "Project",
        label: meta.name,
        attrs: { source: "linked-foyr-pending" },
      }],
      edges: [],
    };
  } catch { return SAMPLE_PROJECT; }
}
